import { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Radio } from "antd";
import { v4 as uuidv4 } from "uuid";

import CurrencyInput from "react-currency-input-field";
import { Input, Select } from "antd";
import {
  companyTypeOptions,
  countryOptions,
  industryOptions,
  capitalizationOptions,
  stocksExchangeOptions,
  currencyOptions,
  sizeOptions
} from "../../data/data";

import dayjs from "dayjs";

import utc from "dayjs-plugin-utc";

import "./organizationForm.css";
import StyledLabel from "../shared/StyledLabel";
import LabelWithError from "../shared/LabelWithError";

import { createOrganization } from "../../store/organizations/actions";
import { sortOptions } from "../../util/sorters";

dayjs.extend(utc);

const OrganizationForm = props => {
  const dispatchito = useDispatch();

  const user = useSelector(state => state.user);
  const organizations = useSelector(state => state.organizations.organizations);

  const [intlConfig, setIntlConfig] = useState(currencyOptions[0]);
  const [countOrganizationName, setCountOrganizationName] = useState(uuidv4());
  const [countCompanyCountry, setCountCompanyCountry] = useState(uuidv4());
  const [countCompanyType, setCountCompanyType] = useState(uuidv4());
  const [countIndustries, setCountIndustries] = useState(uuidv4());
  const [countCapitalization, setCountCapitalization] = useState(uuidv4());
  const [countParentCompany, setCountParentCompany] = useState(uuidv4());
  const [fiscalRadioChoice, setFiscalRadioChoice] = useState("30/6"); //created to update chosen radio buttons visually

  //just a flag to show/hide parent company select. initial value to hide select. not sent back.
  const [isParent, setIsParent] = useState(true);

  const [regulated, setRegulated] = useState(false);


  const [disableSubmit, setDisableSubmit] = useState(false);

  const jun = dayjs("2023/06/30", "YYYY/MM/DD");
  const aug = dayjs("2023/12/31", "YYYY/MM/DD");

  const parentCompanyOptions =
    props.organizations?.map(item => {
      // console.debug(item.customerName, item._id);
      return { value: item._id, label: item.customerName };
    }) ?? [];
  // console.debug("INSIDE OrganizationForm.jsx - props:", props);
  // console.debug("parentCompanyOptions:", parentCompanyOptions);

  //form fields
  const [customerName, setCustomerName] = useState("");
  const [companyCountry, setCompanyCountry] = useState("");
  const [fiscalEndDate, setFiscalEndDate] = useState("30/6");
  const [taxId, setTaxId] = useState("");
  const [parentCompany, setParentCompany] = useState("");
  const [companyType, setCompanyType] = useState("");
  const [industries, setIndustries] = useState([]);
  const [isListed, setIsListed] = useState(false);
  const [underSupervision, setUnderSupervision] = useState(false);
  const [suspended, setSuspended] = useState(false);
  const [capitalization, setCapitalization] = useState("Unknown");
  const [stocksExchange, setStocksExchange] = useState("ATHEX");
  const [numberOfEmployees, setNumberOfEmployees] = useState(0);
  const [turnover, setTurnover] = useState(0);
  const [totalAssets, setTotalAssets] = useState(0);
  const [balanceSheet, setBalanceSheet] = useState(0);
  const [size, setSize] = useState("");
  const [isPartOfGroup, setIsPartOfGroup] = useState(false);
  const [nameError, setNameError] = useState(false);
  const [taxError, setTaxError] = useState(false);

  //error message
  const [error, setError] = useState(null);
  const [errorMessageNumberOfEmployees, setErrorMessageNumberOfEmployees] =
    useState(null);

  const [emptyFields, setEmptyFields] = useState([]);

  useEffect(() => {
    if (organizations?.map(org => org.customerName)?.find((value) => value===customerName) && organizations?.map(org => org.taxId)?.find((value) => value===taxId)) {
      setDisableSubmit(true);
      setNameError(true);
      setTaxError(true);
    }
    else if (organizations?.map(org => org.customerName)?.find((value) => value===customerName)) {
      setDisableSubmit(true);
      setNameError(true);
      setTaxError(false);
    }
    else if (organizations?.map(org => org.taxId)?.find((value) => value===taxId)) {
      setDisableSubmit(true);
      setTaxError(true);
      setNameError(false);
    }
    else if (customerName && fiscalEndDate && taxId && size) {
      setDisableSubmit(false);
      setNameError(false);
      setTaxError(false);
    } else {
      setDisableSubmit(true);
      setNameError(false);
      setTaxError(false);
    }
  }, [customerName, fiscalEndDate, taxId, size, setDisableSubmit]);

  const onIsGroupRadioChange = e => {
    // console.debug("is group radio checked", e.target.value);
    setIsPartOfGroup(e.target.value);
    if (!e.target.value) {
      //if isPartOfGroup is false
      setParentCompany(""); //reset parentCompany
    }
  };

  const onFiscalEndRadioChange = e => {
    // console.debug(
    //   "fiscal end date radio checked",
    //   dayjs.utc(e.target.value, "YYYY/MM/DD"),
    //   e.target.value
    // );
    //setFiscalEndDate(dayjs.utc(e.target.value, "YYYY/MM/DD"));
    setFiscalEndDate(e.target.value);
    setFiscalRadioChoice(e.target.value);
  };

  const onIsParentRadioChange = e => {
    setIsParent(e.target.value);
    if (e.target.value) {
      //if isParent is true
      setParentCompany(""); //reset parentCompany
    }
  };

  const onIsListedRadioChange = e => {
    // console.debug("is listed radio checked", e.target.value);
    setIsListed(e.target.value);
  };

  const onRegulatedRadioChange = e => {
    // console.debug("regulated radio checked", e.target.value);
    setRegulated(e.target.value);
  };

  const onUnderSupervisionRadioChange = e => {
    // console.debug("under supervision radio checked", e.target.value);
    setUnderSupervision(e.target.value);
  };

  const onSuspendedRadioChange = e => {
    // console.debug("suspended radio checked", e.target.value);
    setSuspended(e.target.value);
  };

  //select handlers
  const handleCompanyCountryChange = selectedOption => {
    setCompanyCountry(selectedOption);
    // console.debug(`Company Country Option selected:`, selectedOption);
  };

  const handleCompanyTypeChange = selectedOption => {
    setCompanyType(selectedOption);
    // console.debug(`Company Type Option selected:`, selectedOption);
  };

  const handleSizeChange = selectedOption => {
    setSize(selectedOption);
    // console.debug(`Size Option selected:`, selectedOption);
  };

  const handleStocksExchangeChange = selectedOption => {
    setStocksExchange(selectedOption);
    // console.debug(`Stocks Exchange Option selected:`, selectedOption);
  };

  const handleIndustriesChange = selectedOption => {
    setIndustries(() => selectedOption.map(option => option));
    // console.debug(`Industries Option selected:`, selectedOption);
  };

  const handleCapitalizationChange = selectedOption => {
    setCapitalization(selectedOption);
    // console.debug(`Capitalization Option selected:`, selectedOption);
  };

  const handleParentChange = selectedOption => {
    setParentCompany(selectedOption);
    // console.debug(`Parent Option selected:`, selectedOption);
  };

  //handlers
  const handleVatInputChange = e => {
    const value = e.target.value;
    const regex = /^\d{9}$/; // Greek VAT number format: 9 digits

    if (!regex.test(value)) {
      if (!emptyFields?.includes("taxId")) {
        setEmptyFields([...emptyFields, "taxId"]);
      }
    } else {
      setEmptyFields(emptyFields.filter(field => field !== "taxId"));
    }

    setTaxId(value);
  };

  const handleNumberOfEmployeesChange = e => {
    let value = e.target.value;

    // Check if the value is less than 0, if so, set it to 0
    if (value < 0) {
      value = 0;
      setErrorMessageNumberOfEmployees("Wrong value");
    } else {
      setErrorMessageNumberOfEmployees(null);
    }

    setNumberOfEmployees(value);
  };

  // Filter `option.label` match the user type `input`
  const filterOption = (input, option) =>
    (option?.label ?? "").toLowerCase().includes(input.toLowerCase());

  const handleSubmit = async e => {
    e.preventDefault();
    setDisableSubmit(true);

    //const formData = new FormData();
    // console.debug("Empty fields", emptyFields);

    if (!user) {
      setError("You need to be logged in to add an organization");
      return;
    }

    const organization = {
      customerName,
      companyCountry,
      //fiscalEndDate: format(fiscalEndDate, "yyyy-MM-dd"),
      fiscalEndDate,
      taxId,
      parentCompany: parentCompany === "" ? null : parentCompany,
      companyType,
      size,
      industries,
      isRegulated: regulated,
      capitalization,
      isListed,
      stocksExchange,
      numberOfEmployees,
      underSupervision,
      suspended,
      turnover,
      totalAssets,
      balanceSheet,
      isPartOfGroup
    };
    
    dispatchito(createOrganization(organization,user));

    setCustomerName("");
    setCompanyCountry("");
    setTaxId("");
    setFiscalEndDate("30/6");
    setFiscalRadioChoice("30/6");
    setIsPartOfGroup(false);
    setIsParent(true);
    setParentCompany("");
    setSize("");
    setCompanyType("");
    setIndustries([]);
    setCapitalization("Unknown");
    setRegulated(false);
    setIsListed(false);
    setUnderSupervision(false);
    setSuspended(false);
    setStocksExchange("ATHEX");
    setNumberOfEmployees(0);
    setTurnover(0);
    setBalanceSheet(0);
    setTotalAssets(0);
    setError(null);
    setEmptyFields([]);
  };

  return (
    <form className="create" onSubmit={handleSubmit} id="organization-form">
      <h3>Add New Organization</h3>

      <div className="fieldWrapper">
        <StyledLabel
          required
          style={{
            color: "#344054",
            fontFamily: "Roboto",
            fontSize: "0.875rem",
            fontStyle: "normal",
            fontWeight: "500",
            lineHeight: "1.25rem",
            marginTop: "0"
          }}
        >
          Organization Name:
        </StyledLabel>
        <Input
          size="large"
          key={countOrganizationName}
          error={nameError ? "Invalid Name" : null}
          id="organization-name-input"
          type="text"
          onChange={e => setCustomerName(e.target.value)}
          value={customerName}
          className={emptyFields?.includes("customerName") ? "error" : ""}
          style={{ paddingTop: "0.563rem", paddingBottom: "0.563rem" }}
          placeholder="Organization Name"
        />
        <label style={{color:"red"}}>
          {nameError ? "Name already exists" : null}
        </label>
      </div>

      <div className="fieldWrapper">
        <StyledLabel
          required
          style={{
            color: "#344054",
            fontFamily: "Roboto",
            fontSize: "0.875rem",
            fontStyle: "normal",
            fontWeight: "500",
            lineHeight: "1.25rem",
            marginTop: "0",
            paddingTop: "1.25rem"
          }}
        >
          Organization Country:
        </StyledLabel>
        <Select
          virtual={false}
          showSearch
          placeholder="Select"
          style={{ width: "100%" }}
          key={countCompanyCountry}
          id="company-country-select"
          options={countryOptions}
          onChange={handleCompanyCountryChange}
          className="react-select-container"
          size="large"
        />
      </div>

      <div id="fiscal-end-date">
        <StyledLabel
          required
          style={{
            color: "#344054",
            fontFamily: "Roboto",
            fontSize: "0.875rem",
            fontStyle: "normal",
            fontWeight: "500",
            lineHeight: "1.25rem",
            marginTop: "1.5rem"
          }}
        >
          Fiscal End Date:
        </StyledLabel>
        <Radio.Group
          onChange={onFiscalEndRadioChange}
          value={fiscalRadioChoice}
        >
          <Radio value={"30/6"}>30/6</Radio>
          <Radio value={"31/12"}>31/12</Radio>
        </Radio.Group>
      </div>

      <div id="tax-id" style={{ marginTop: "0" }}>
        <LabelWithError
          required
          // error={emptyFields?.includes("taxId") ? "Invalid VAT number" : taxError ? "Invalid VAT number" : null}
          style={{
            color: "#344054",
            fontFamily: "Roboto",
            fontSize: "0.875rem",
            fontStyle: "normal",
            fontWeight: "500",
            lineHeight: "1.25rem"
          }}
        >
          Tax ID:
        </LabelWithError>
        <Input
          size="large"
          placeholder="Enter a valid Tax ID"
          type="text"
          id="organizations-tax-input"
          onChange={handleVatInputChange}
          value={taxId}
          className={emptyFields?.includes("taxId") ? "error" : ""}
          style={{ paddingTop: "0.563rem", paddingBottom: "0.563rem" }}
        />
        <label style={{color:"red"}}>
          {taxError ? "Tax ID already exists" : null}
        </label>
      </div>

      <div id="company-size" style={{ marginTop: "1.25rem" }}>
        <StyledLabel
          required
          htmlFor="company-size-select"
          style={{
            color: "#344054",
            fontFamily: "Roboto",
            fontSize: "0.875rem",
            fontStyle: "normal",
            fontWeight: "500",
            lineHeight: "1.25rem"
          }}
        >
          Company Size:
        </StyledLabel>
        <Select
          virtual={false}
          style={{ width: "100%" }}
          id="company-size-select"
          name="size"
          options={sizeOptions}
          className="basic-multi-select"
          onChange={handleSizeChange}
          value={size || undefined}
          size="large"
          placeholder="Select"
        />
      </div>

      <div id="isGroup" style={{ marginTop: "1.25rem" }}>
        {/* <StyledLabel required>Is a Group of Companies</StyledLabel> */}
        <StyledLabel
          required
          style={{
            color: "#344054",
            fontFamily: "Roboto",
            fontSize: "0.875rem",
            fontStyle: "normal",
            fontWeight: "500",
            lineHeight: "1.25rem"
          }}
        >
          Is it part of a Group?
        </StyledLabel>
        <Radio.Group onChange={onIsGroupRadioChange} value={isPartOfGroup}>
          <Radio value={true}>Yes</Radio>
          <Radio value={false}>No</Radio>
        </Radio.Group>
      </div>

      {isPartOfGroup && (
        <>
          <div id="isParent" style={{ marginTop: "1.25rem" }}>
            {/* <StyledLabel required>Is a Group of Companies</StyledLabel> */}
            <StyledLabel>Is it a Parent Company?</StyledLabel>
            <Radio.Group onChange={onIsParentRadioChange} value={isParent}>
              <Radio value={true}>Yes</Radio>
              <Radio value={false}>No</Radio>
            </Radio.Group>
          </div>
          {!isParent && (
            <div id="parent-company" style={{ marginTop: "1.25rem" }}>
              <StyledLabel style={{ marginBottom: "0.625rem" }}>
                Name of Parent Company:
              </StyledLabel>
              <Select
                virtual={false}
                showSearch
                filterOption={filterOption}
                style={{ width: "100%", height: "2.313rem" }}
                allowClear={true}
                placeholder="Select"
                id="parent-company-select"
                key={countParentCompany}
                options={parentCompanyOptions}
                onChange={handleParentChange}
                size="large"
                className="react-select-container"
              />
            </div>
          )}
        </>
      )}

      <div id="company-type" style={{ marginTop: "1.25rem" }}>
        <StyledLabel
          style={{
            marginBottom: "0.625rem",
            color: "#344054",
            fontFamily: "Roboto",
            fontSize: "0.875rem",
            fontStyle: "normal",
            fontWeight: "500",
            lineHeight: "1.25rem"
          }}
        >
          Company Type:
        </StyledLabel>
        <Select
          virtual={false}
          placeholder="Select"
          style={{ width: "100%", height: "2.313rem" }}
          key={countCompanyType}
          id="company-type-select"
          options={companyTypeOptions}
          onChange={handleCompanyTypeChange}
          className="react-select-container"
          size="large"
        />
      </div>

      <div id="industries" style={{ marginTop: "1.25rem" }}>
        <StyledLabel
          style={{
            marginBottom: "0.625rem",
            color: "#344054",
            fontFamily: "Roboto",
            fontSize: "0.875rem",
            fontStyle: "normal",
            fontWeight: "500",
            lineHeight: "1.25rem"
          }}
        >
          Industries:
        </StyledLabel>
        <Select
          virtual={false}
          mode="multiple"
          placeholder="Select"
          style={{ width: "100%" }}
          key={countIndustries}
          id="industries-select"
          name="industries"
          options={sortOptions(industryOptions)}
          className="basic-multi-select"
          onChange={handleIndustriesChange}
          size="large"
        />
      </div>

      <div id="regulated" style={{ marginTop: "1.25rem" }}>
        <StyledLabel
          style={{
            marginBottom: "0.625rem",
            color: "#344054",
            fontFamily: "Roboto",
            fontSize: "0.875rem",
            fontStyle: "normal",
            fontWeight: "500",
            lineHeight: "1.25rem"
          }}
        >
          Regulated:
        </StyledLabel>
        <Radio.Group
          id="regulated-radio-group"
          onChange={onRegulatedRadioChange}
          value={regulated}
        >
          <Radio id="regulated-option-yes" value={true}>
            Yes
          </Radio>
          <Radio id="regulated-option-no" value={false}>
            No
          </Radio>
        </Radio.Group>
      </div>

      <div
        id="isListed"
        style={{
          marginTop: "1.25rem",
          color: "#344054",
          fontFamily: "Roboto",
          fontSize: "0.875rem",
          fontStyle: "normal",
          fontWeight: "500",
          lineHeight: "1.25rem"
        }}
      >
        <StyledLabel style={{ marginBottom: "0.625rem" }}>Is Listed:</StyledLabel>
        <Radio.Group onChange={onIsListedRadioChange} value={isListed}>
          <Radio value={true} id="islisted-yes">
            Yes
          </Radio>
          <Radio value={false} id="islisted-no">
            No
          </Radio>
        </Radio.Group>
      </div>

      {isListed && (
        <>
          <div id="capitalization" style={{ marginTop: "1.25rem" }}>
            <StyledLabel style={{ marginBottom: "0.625rem" }}>
              Capitalization:
            </StyledLabel>
            <Select
              virtual={false}
              id="capitalization-select"
              placeholder=""
              style={{ width: "100%", height: "2.313rem" }}
              key={countCapitalization}
              options={capitalizationOptions}
              onChange={handleCapitalizationChange}
              className="react-select-container"
              size="large"
            />
          </div>
          <div id="stock-exchange" style={{ marginTop: "1.25rem" }}>
            <StyledLabel style={{ marginBottom: "0.625rem" }}>
              Stocks Exchange:
            </StyledLabel>
            <Select
              virtual={false}
              style={{ width: "100%", height: "2.313rem" }}
              id="stock-ex-select"
              options={stocksExchangeOptions}
              onChange={handleStocksExchangeChange}
              className="react-select-container"
              value={stocksExchange}
              size="large"
            />
          </div>
          <div id="underSupervision" style={{ marginTop: "1.25rem" }}>
            <StyledLabel style={{ marginBottom: "0.625rem" }}>
              Under Supervision
            </StyledLabel>
            <Radio.Group
              onChange={onUnderSupervisionRadioChange}
              value={underSupervision}
              id="supervised-radio-group"
            >
              <Radio id="supervised-option-yes" value={true}>
                Yes
              </Radio>
              <Radio id="supervised-option-no" value={false}>
                No
              </Radio>
            </Radio.Group>
          </div>
          <div id="suspended" style={{ marginTop: "1.25rem" }}>
            <StyledLabel style={{ marginBottom: "0.625rem" }}>
              Suspended
            </StyledLabel>
            <Radio.Group
              id="suspended-radio-group"
              onChange={onSuspendedRadioChange}
              value={suspended}
            >
              <Radio id="suspended-option-yes" value={true}>
                Yes
              </Radio>
              <Radio id="suspended-option-no" value={false}>
                No
              </Radio>
            </Radio.Group>
          </div>
        </>
      )}

      <div id="number-of-employees" style={{ marginTop: "1.25rem" }}>
        <LabelWithError error={errorMessageNumberOfEmployees}>
          Number of Employees:
        </LabelWithError>
        <Input
          type="number"
          id="number-of-employees-input"
          onChange={handleNumberOfEmployeesChange}
          value={numberOfEmployees}
          min="0"
          size="large"
        />
      </div>

      <div id="turnover" style={{ marginTop: "1.25rem" }}>
        <StyledLabel>Turnover (in {"\u20AC"}):</StyledLabel>
        <CurrencyInput
          allowNegativeValue={false}
          className="react-currency-input-field"
          decimalsLimit={6}
          intlConfig={intlConfig}
          id="turnover-input"
          name="turnover-input-name"
          placeholder="Please enter a number"
          value={turnover}
          onValueChange={value => setTurnover(value)}
          size="large"
        />
      </div>

      <div id="total-assets" style={{ marginTop: "1.25rem" }}>
        <StyledLabel>Total Assets (in {"\u20AC"}):</StyledLabel>
        <CurrencyInput
          allowNegativeValue={false}
          className="react-currency-input-field"
          decimalsLimit={6}
          intlConfig={intlConfig}
          id="totals-assets-input"
          name="total-assets-input-name"
          placeholder="Please enter a number"
          value={totalAssets}
          onValueChange={value => setTotalAssets(value)}
          size="large"
        />
      </div>

      <div id="balance-sheet" style={{ marginTop: "1.25rem" }}>
        <StyledLabel>Balance Sheet (in {"\u20AC"}):</StyledLabel>
        <CurrencyInput
          allowNegativeValue={false}
          className="react-currency-input-field"
          decimalsLimit={6}
          intlConfig={intlConfig}
          id="balance-sheet-input"
          name="balance-sheet-input-name"
          placeholder="Please enter a number"
          value={balanceSheet}
          onValueChange={value => setBalanceSheet(value)}
          size="large"
        />
      </div>

      <button
        style={{ width: "100%" }}
        id="submitButton"
        disabled={emptyFields?.length > 0 || disableSubmit} // Disable the button if there is any error
        onClick={() => {
          setCountOrganizationName(uuidv4());
          setCountCompanyType(uuidv4());
          setCountIndustries(uuidv4());
          setCountCapitalization(uuidv4());
          setCountCompanyCountry(uuidv4());
          window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
        }}
      >
        Add Organization
      </button>
      {error && <div className="error">{error}</div>}
    </form>
  );
};

export default OrganizationForm;
